import { Button, Prompt, StepBlock } from '@components'
import { useBusinessContext, useFeature } from '@context'
import { useDebugMode } from '@hooks'
import { IPeriod, performPeriodAction, PeriodActionType } from '@query'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCheckCircle, FaLock, FaMask, FaTimes } from 'react-icons/fa'
import ReactMarkdown from 'react-markdown'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Paragraph } from './PeriodSteps.styled'
import { PeriodValidate } from './PeriodValidate'
import styled from 'styled-components'

interface Props {
  period: IPeriod
}

export const PeriodSteps: React.FC<Props> = ({ period }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { enabled: yleTaxEnabled } = useFeature('yle_tax')
  const queryClient = useQueryClient()
  const [debugModeOn] = useDebugMode()
  const [showLock, setShowLock] = useState(false)

  const actionMutation = useMutation<void, unknown, PeriodActionType>({
    mutationFn: async action => {
      performPeriodAction({ action, slug, periodId: period.id })
    },
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'periods'] })
        await queryClient.invalidateQueries({ queryKey: [slug, 'periods', period.id] })
        await queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
        toast.success(t('general.success'))
      },
      onError: () => {
        toast.error(t('error.genericRetry'))
      }
    }
  })

  if (debugModeOn) {
    return (
      <>
        <StepBlock
          title={t('period.steps.wait.title')}
          isBlocked={false}
          isCompleted={period.has_ended}
          descCurrent={<Paragraph>{t('period.steps.wait.current')}</Paragraph>}
          descComplete={<Paragraph className="muted">{t('period.steps.wait.complete')}</Paragraph>}
        />

        <StepBlock
          title={t('period.steps.ensure.title')}
          isBlocked={!period.has_ended}
          isCompleted={period.is_ensured}
          descCurrent={
            <>
              <Paragraph>{t('period.steps.ensure.current1')}</Paragraph>
              <Paragraph>{t('period.steps.ensure.current2')}</Paragraph>
              <PeriodValidate period={period} />
              <Paragraph>{t('period.steps.ensure.current3')}</Paragraph>
              <ButtonRow $align="left">
                <Button
                  type="button"
                  size="sm"
                  intent="primary"
                  icon={<FaCheckCircle size={12} />}
                  disabled={period.is_ensured}
                  onClick={() => actionMutation.mutate('ensure')}
                >
                  {t('period.steps.ensure.continueButton')}
                </Button>
              </ButtonRow>
            </>
          }
          descComplete={
            <Paragraph className="muted">{t('period.steps.ensure.complete')}</Paragraph>
          }
        />

        <StepBlock
          title={t('period.steps.depreciations.title')}
          isBlocked={!period.is_ensured}
          isCompleted={period.is_depreciations_ensured}
          descCurrent={
            <>
              <Paragraph>{t('period.steps.depreciations.current1')}</Paragraph>
              <Paragraph>{t('period.steps.depreciations.current2')}</Paragraph>
              {period.is_depreciations_recorded ? (
                <ButtonRow $align="left">
                  <Button
                    type="button"
                    size="sm"
                    intent="primary"
                    icon={<FaCheckCircle size={12} />}
                    disabled={period.is_depreciations_ensured}
                    onClick={() => actionMutation.mutate('ensure_depreciations')}
                  >
                    {t('period.steps.depreciations.continueButton')}
                  </Button>
                </ButtonRow>
              ) : (
                <>
                  <Paragraph>{t('period.steps.depreciations.current3')}</Paragraph>
                  <ButtonRow $align="left">
                    <Button
                      type="button"
                      size="sm"
                      intent="primary"
                      icon={<FaCheckCircle size={12} />}
                      disabled={period.is_depreciations_recorded}
                      onClick={() => actionMutation.mutate('record_depreciations')}
                    >
                      {t('period.steps.depreciations.createDepreciations')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      icon={<FaTimes size={12} />}
                      disabled={period.is_depreciations_recorded}
                      onClick={() => actionMutation.mutate('skip_record_depreciations')}
                    >
                      {t('period.steps.depreciations.skipDepreciations')}
                    </Button>
                  </ButtonRow>
                </>
              )}
            </>
          }
          descComplete={
            <Paragraph className="muted">{t('period.steps.depreciations.complete')}</Paragraph>
          }
        />

        <StepBlock
          title={t('period.steps.deferrals.title')}
          isBlocked={!period.is_depreciations_ensured}
          isCompleted={period.is_deferrals_recorded}
          descCurrent={
            <>
              <Paragraph>{t('period.steps.deferrals.current1')}</Paragraph>
              <Paragraph>{t('period.steps.deferrals.current2')}</Paragraph>
              <Paragraph>{t('period.steps.deferrals.current3')}</Paragraph>

              <ButtonRow $align="left">
                <Button
                  type="button"
                  size="sm"
                  intent="primary"
                  icon={<FaCheckCircle size={12} />}
                  disabled={period.is_deferrals_recorded}
                  onClick={() => actionMutation.mutate('deferrals_recorded')}
                >
                  {t('period.steps.deferrals.continueButton')}
                </Button>
              </ButtonRow>
            </>
          }
          descComplete={
            <Paragraph className="muted">{t('period.steps.deferrals.complete')}</Paragraph>
          }
        />

        <StepBlock
          title={t('period.steps.taxes.title')}
          isBlocked={!period.is_deferrals_recorded}
          isCompleted={period.is_taxed}
          descCurrent={
            <>
              <Paragraph>
                {t(
                  yleTaxEnabled
                    ? 'period.steps.taxes.current'
                    : 'period.steps.taxes.currentWithoutYle'
                )}
              </Paragraph>
              <ButtonRow $align="left">
                <Button
                  type="button"
                  size="sm"
                  intent="primary"
                  icon={<FaMask size={12} />}
                  disabled={period.is_taxed}
                  onClick={() => actionMutation.mutate('record_taxes')}
                >
                  {t('period.steps.taxes.continueButton')}
                </Button>
              </ButtonRow>
            </>
          }
          descComplete={<Paragraph className="muted">{t('period.steps.taxes.complete')}</Paragraph>}
        />

        <StepBlock
          title={t('period.steps.lock.title')}
          isBlocked={!period.is_taxed}
          isCompleted={period.is_locked}
          descCurrent={
            <>
              <Paragraph>{t('period.steps.lock.current1')}</Paragraph>
              <Paragraph>{t('period.steps.lock.current2')}</Paragraph>
              <ButtonRow $align="left">
                <Button
                  type="button"
                  size="sm"
                  intent="primary"
                  icon={<FaLock size={12} />}
                  disabled={period.is_locked}
                  onClick={() => actionMutation.mutate('lock')}
                >
                  {t('period.steps.lock.continueButton')}
                </Button>
              </ButtonRow>
            </>
          }
          descComplete={<Paragraph color="muted">{t('period.steps.lock.complete')}</Paragraph>}
        />

        <StepBlock
          title={t('period.steps.report.title')}
          isBlocked={!period.is_locked}
          isCompleted={period.is_reported}
          descCurrent={
            <>
              <Paragraph>{t('period.steps.report.current')}</Paragraph>
              <ButtonRow $align="left">
                <Button
                  type="button"
                  size="sm"
                  intent="primary"
                  icon={<FaCheckCircle size={12} />}
                  disabled={period.is_reported}
                  onClick={() => actionMutation.mutate('report')}
                >
                  {t('period.steps.report.continueButton')}
                </Button>
              </ButtonRow>
            </>
          }
          descComplete={<Paragraph color="muted">{t('period.steps.report.complete')}</Paragraph>}
          isLast={true}
        />
      </>
    )
  } else {
    return (
      <>
        <StepBlock
          title={t('period.steps.wait.title')}
          isBlocked={false}
          isCompleted={period.has_ended}
          descCurrent={<Paragraph>{t('period.steps.wait.current')}</Paragraph>}
          descComplete={<Paragraph className="muted">{t('period.steps.wait.complete')}</Paragraph>}
        />
        <StepBlock
          title={t('period.steps.lock.title')}
          isBlocked={!period.has_ended}
          isCompleted={period.is_locked}
          isLast={true}
          descCurrent={
            <>
              <Paragraph>{t('period.steps.lock.currentTemp')}</Paragraph>
              <ButtonRow $align="left">
                <Button
                  type="button"
                  size="sm"
                  intent="primary"
                  icon={<FaLock size={12} />}
                  disabled={period.is_locked}
                  onClick={() => setShowLock(true)}
                >
                  {t('period.steps.lock.continueButton')}
                </Button>
              </ButtonRow>
            </>
          }
          descComplete={<Paragraph color="muted">{t('period.steps.lock.complete')}</Paragraph>}
        />

        <Prompt
          title={t('period.steps.lock.ensureTitle')}
          description={<ReactMarkdown>{t('period.steps.lock.ensureDescription')}</ReactMarkdown>}
          isVisible={showLock}
          onClose={() => setShowLock(false)}
          buttons={[
            {
              text: t('general.cancel'),
              action: () => setShowLock(false)
            },
            {
              text: t('period.steps.lock.ensureButton'),
              action: async () => {
                await actionMutation.mutateAsync('lock')
                setShowLock(false)
              },
              icon: <FaLock />,
              intent: 'primary'
            }
          ]}
        />
      </>
    )
  }
}

const ButtonRow = styled.div<{ $align: 'left' | 'right' }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin: 4px 0;
`
